import { z } from "zod";

/* ===========================================================================
 * US-E7.F2-0050 — Member Vote Expense (@UC0067/@UC0068)
 * POST /v1/expenses/:id/votes + GET /v1/expenses/:id/vote-status.
 *
 * PO chốt: phiếu MUTABLE TRƯỚC deadline (đổi choice = update); sau deadline/đóng → khóa.
 * Auto-finalize khi đủ tỉ lệ sớm hoặc deadline → passed post EXPENSE | failed rejected.
 * ========================================================================= */

export const VOTE_CHOICE = {
  YES: "yes",
  NO: "no",
  ABSTAIN: "abstain",
} as const;

export type VoteChoice = (typeof VOTE_CHOICE)[keyof typeof VOTE_CHOICE];

export const voteChoiceSchema = z.enum([
  VOTE_CHOICE.YES,
  VOTE_CHOICE.NO,
  VOTE_CHOICE.ABSTAIN,
]);

export const VOTE_CLOSED_MESSAGE =
  "Phiên biểu quyết đã đóng — không thể bỏ phiếu.";

/**
 * 🔴 US-AIV2-0112 CONSENSUS-ADULT (@UC0354 · QĐ PO 30/09 P1) — 422: mở phiên biểu quyết mà KHÔNG có ai được bỏ phiếu.
 * Nhóm phụ huynh chỉ có người lớn (0 suất bé) ⇒ mẫu số 0. Fail-closed ở MỌI cửa mở phiên (chi · khẩn cấp · quản trị · vòng đời):
 * không tạo phiên, không bút toán. Không mượn mẫu số "đếm sống" của phiên cũ (`eligibleCount = 0` ⇒ đếm lại — sẽ đếm cả người lớn).
 */
export const CONSENSUS_NO_ELIGIBLE_VOTER_CODE = "CONSENSUS_NO_ELIGIBLE_VOTER" as const;

/**
 * 🔴 US-AIV2-0112 CONSENSUS-ADULT (@UC0355 · QĐ PO 30/09 "mỗi bé 1 phiếu") — 403: người bấm không có phiếu nào trong phiên này.
 * Nhóm phụ huynh, phiên mở sau bản vá: phiếu thuộc SUẤT BÉ; người lớn không đại diện suất bé nào (cô giáo) ⇒ không có phiếu.
 * 0 dòng `votes` được ghi.
 */
export const CONSENSUS_VOTER_NOT_ELIGIBLE_CODE = "CONSENSUS_VOTER_NOT_ELIGIBLE" as const;

/**
 * 🔴 US-AIV2-0112 CONSENSUS-FIRST-VOTE (@UC0356 · QĐ PO 30/09 "PHIẾU ĐẦU THẮNG") — 409: MỌI suất bé trong lá phiếu của người bấm
 * đã có phiếu của NGƯỜI KHÁC (phụ huynh / người đại diện bấm trước). Không đè, 0 dòng `votes` đổi. `params`:
 * `seatIds` (id suất, nối bằng dấu phẩy) · `seatNames` (tên suất theo luật tên chung, nối ", "; suất chưa đặt tên bị bỏ) · `count`.
 * Chỉ một phần lá phiếu đã có chủ ⇒ KHÔNG 409: ghi cho suất còn trống, response 200 kèm `seatsVotedByOthers`.
 */
export const CONSENSUS_SEAT_ALREADY_VOTED_CODE = "CONSENSUS_SEAT_ALREADY_VOTED" as const;

/**
 * Suất bé đã có phiếu của người khác — trả trong response bỏ phiếu 200 (3 cửa) để người bấm biết lá phiếu của họ KHÔNG ghi cho suất
 * này (không bao giờ bỏ qua im lặng). Nhóm thường / phiên luật cũ ⇒ luôn `[]`.
 */
export const seatVotedByOtherSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    /** Tên suất theo luật tên chung (biệt danh — không mượn họ tên người lớn); `null` = suất chưa đặt tên. */
    name: z.string().nullable(),
  })
  .strict();

export type SeatVotedByOther = z.infer<typeof seatVotedByOtherSchema>;

/**
 * 🔴 US-AIV2-0112 CONSENSUS-MOBILE (@UC0357 · review #1084 M3) — lá phiếu của NGƯỜI XEM, báo TRƯỚC khi bấm. Có ở 3 cửa ĐỌC phiên
 * (`vote-status` khoản chi · chi tiết phiên quản trị · chi tiết phiên vòng đời). Server dẫn xuất từ CÙNG lá phiếu và CÙNG luật chia
 * mà cổng ghi dùng (`resolveConsensusBallot` + `splitBallotSeats`) ⇒ `canVote = false` đúng khi lượt bấm ngay sau sẽ nhận 403
 * `CONSENSUS_VOTER_NOT_ELIGIBLE` hoặc 409 `CONSENSUS_SEAT_ALREADY_VOTED`. Client ẩn nút theo cờ này, KHÔNG tự suy luật.
 *  · `canVote` — người xem còn ít nhất một phiếu ghi được (suất của chính mình, hoặc suất chưa ai bỏ). KHÔNG xét phiên đã đóng / hết hạn:
 *    trạng thái phiên đã có ở trường trạng thái của response.
 *  · `ballotSeats` — phiên "mỗi bé 1 phiếu": các suất bé mà một lần bấm của người xem sẽ ghi phiếu. Luật ghế (nhóm thường, phiên cũ) ⇒
 *    luôn `[]` (một người một ghế, không có bé nào để liệt kê).
 *  · `seatsVotedByOthers` — suất bé trong lá phiếu của người xem đã có phiếu của NGƯỜI KHÁC (phiếu đầu thắng) — không tính cho người xem.
 * Tên suất theo luật tên chung (biệt danh — không mượn họ tên người lớn); `null` = suất chưa đặt tên.
 */
export const consensusBallotViewSchema = z
  .object({
    canVote: z.boolean(),
    ballotSeats: z.array(seatVotedByOtherSchema),
    seatsVotedByOthers: z.array(seatVotedByOtherSchema),
  })
  .strict();

export type ConsensusBallotView = z.infer<typeof consensusBallotViewSchema>;

/** Body vote. choice yes/no/abstain. */
export const castVoteRequestSchema = z
  .object({
    choice: voteChoiceSchema,
  })
  .strict();

export type CastVoteRequest = z.infer<typeof castVoteRequestSchema>;

/** Bộ đếm phiếu (dùng cho cast response + vote-status). */
export const voteTallySchema = z
  .object({
    yes: z.number().int().nonnegative(),
    no: z.number().int().nonnegative(),
    abstain: z.number().int().nonnegative(),
    totalEligible: z.number().int().nonnegative(),
  })
  .strict();

export type VoteTally = z.infer<typeof voteTallySchema>;

/** Consensus status sau cast (open nếu chưa chốt; passed/failed/expired nếu finalize). */
export const consensusStatusSchema = z.enum([
  "open",
  "passed",
  "failed",
  "expired",
]);

export const castVoteResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    consensusId: z.string().uuid(),
    myVote: voteChoiceSchema,
    tally: voteTallySchema,
    consensusStatus: consensusStatusSchema,
    /** txId EXPENSE nếu vote này làm consensus passed + post ledger; null nếu chưa chốt. */
    ledgerTxId: z.string().uuid().nullable(),
    /** US-AIV2-0112 CONSENSUS-FIRST-VOTE (@UC0356): suất đã có phiếu của người khác — lần bấm này KHÔNG ghi cho chúng. */
    seatsVotedByOthers: z.array(seatVotedByOtherSchema),
  })
  .strict();

export type CastVoteResponse = z.infer<typeof castVoteResponseSchema>;

export const voteStatusResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    consensusId: z.string().uuid(),
    consensusStatus: consensusStatusSchema,
    tally: voteTallySchema,
    /** Lựa chọn của caller | null nếu chưa vote. */
    myVote: voteChoiceSchema.nullable(),
    deadline: z.string().datetime().nullable(),
    /** US-AIV2-0112 CONSENSUS-MOBILE (@UC0357): lá phiếu của người xem — xem `consensusBallotViewSchema`. */
    ballot: consensusBallotViewSchema,
  })
  .strict();

export type VoteStatusResponse = z.infer<typeof voteStatusResponseSchema>;
